import { LoadingState } from "@repo/app-types/client/client";
import axios from "axios";
import { type FC, useEffect, useState } from "react";
import { type OAuthProvider, startOAuthSignIn } from "../../api/oauthSignIn.js";
import {
  openAuthPopup,
  POPUP_AUTH_CALLBACK_QUERY_PARAM_NAME,
  requiresPopupAuth,
} from "../../api/popupAuth.js";
import { apiBaseUrl } from "../../api/shared.js";
import { sendMagicLinkSignIn } from "../../api/signInActions.js";
import { getSupabaseClient } from "../../api/supabaseClient.js";
import { useAuthPopupSession } from "../../hooks/useAuthPopupSession.js";
import {
  InstructionVariant,
  SignInMode,
  SignInScreen,
} from "./signInScreen.js";

export { SALES_DEMO_LINK } from "./signInScreen.js";

/**
 * Container for the sign-in screen. Owns all auth side effects (Supabase OAuth /
 * SSO / magic link, popup auth for embedded Fountain, login-instruction lookup)
 * and feeds a view model to the presentational {@link SignInScreen}.
 *
 * Invitations have their own screen ({@link InvitationSignInScreen}); this is
 * the standard sign-in surface.
 */
export const SignInPage: FC = () => {
  const [instructionVariant, setInstructionVariant] =
    useState<InstructionVariant>(InstructionVariant.STANDARD);
  const [loginInstructionLoadingState, setLoginInstructionLoadingState] =
    useState<LoadingState>(LoadingState.LOADING);
  // Read URL query params and detect iframe embedding once via a lazy state
  // initializer to keep these impure reads out of the render phase.
  const [{ ssoDomainFromUrl, interviewContactName, shouldUsePopupAuth }] =
    useState(() => {
      const queryParams = new URLSearchParams(window.location.search);
      return {
        ssoDomainFromUrl: queryParams.get("sso_domain"),
        interviewContactName: queryParams.get("n"),
        shouldUsePopupAuth: requiresPopupAuth(),
      };
    });

  const [mode, setMode] = useState<SignInMode>(
    ssoDomainFromUrl ? SignInMode.SSO : SignInMode.OAUTH,
  );
  const [customSsoDomain, setSsoDomain] = useState("");
  const ssoDomain = ssoDomainFromUrl || customSsoDomain;
  const [email, setEmail] = useState("");
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [magicLinkSentToEmail, setMagicLinkSentToEmail] = useState<
    string | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchLoginInstructions = async (projectId: string) => {
    try {
      const url = new URL("/domain-login-status", apiBaseUrl);
      url.searchParams.set("project_id", projectId);
      const { data } = await axios.get(url.toString());

      setInstructionVariant(
        data.is_domain_login_available
          ? InstructionVariant.INSIGHTS_WITH_DOMAIN_LOGIN
          : InstructionVariant.INSIGHTS_WITHOUT_DOMAIN_LOGIN,
      );
      setLoginInstructionLoadingState(LoadingState.LOADED);
    } catch (err) {
      // If login instruction check fails, the standard login UI be used.
      setLoginInstructionLoadingState(LoadingState.ERROR);
    }
  };

  useAuthPopupSession({
    isEnabled: shouldUsePopupAuth,
    onError: setErrorMessage,
  });

  useEffect(() => {
    // Try to get the project ID from the URL of a project or interview page.
    const projectId = extractProjectIdFromProjectOrInterviewPage();

    // Only check for domain login possibility if we are on a project or interview page
    // (i.e. the user is trying to access a link to insights)
    if (projectId) {
      fetchLoginInstructions(projectId);
    } else {
      setLoginInstructionLoadingState(LoadingState.LOADED);
    }
  }, []);

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    const { error } = await startOAuthSignIn(provider, {
      usePopup: shouldUsePopupAuth,
    });

    if (error) {
      console.error(error);
    }
  };

  const handleEmailMagicLinkSignIn = async () => {
    setErrorMessage(null);
    setIsSendingMagicLink(true);
    try {
      const { error } = await sendMagicLinkSignIn(email);

      if (error) {
        setErrorMessage(error);
        return;
      }

      setMagicLinkSentToEmail(email);
    } catch (error) {
      console.log("Email magic link sign-in error:", error);
      setErrorMessage("An error occurred sending your link. Please try again.");
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  const handleSSOSignIn = async () => {
    try {
      let options = {};

      // If we're using popup auth, we need to set the query param so the popup knows to send the code back to the parent window
      if (shouldUsePopupAuth) {
        // Prepare a return-to URL only for popup auth
        const redirectTo = new URL(window.location.href);
        redirectTo.hash = "";
        redirectTo.searchParams.set(
          POPUP_AUTH_CALLBACK_QUERY_PARAM_NAME,
          "true",
        );
        options = {
          redirectTo: redirectTo.toString(),
          skipBrowserRedirect: true,
        };
      }

      const { data, error } = await getSupabaseClient().auth.signInWithSSO({
        domain: ssoDomain,
        options,
      });

      if (error) {
        if (error.message.includes("No SSO provider assigned")) {
          setErrorMessage(
            "No SSO provider found for this domain. Please try again.",
          );
          return;
        }
        throw error;
      }

      if (data.url) {
        if (shouldUsePopupAuth) {
          openAuthPopup(data.url);
        } else {
          // Redirect in a normal browser context
          window.location.href = data.url;
        }
      } else {
        setErrorMessage("No SSO URL returned. Please try again.");
      }
    } catch (error) {
      console.log("SSO sign-in error:", error);
      setErrorMessage(
        "An error occurred during SSO sign-in. Please try again.",
      );
    }
  };

  return (
    <SignInScreen
      instructionVariant={instructionVariant}
      mode={mode}
      isLoadingInstructions={
        loginInstructionLoadingState === LoadingState.LOADING
      }
      interviewContactName={interviewContactName}
      ssoDomain={ssoDomain}
      isSsoDomainReadOnly={Boolean(ssoDomainFromUrl)}
      email={email}
      isSendingMagicLink={isSendingMagicLink}
      magicLinkSentToEmail={magicLinkSentToEmail}
      errorMessage={errorMessage}
      onModeChange={(nextMode) => {
        setMode(nextMode);
        setErrorMessage(null);
      }}
      onSsoDomainChange={(value) => {
        setSsoDomain(value);
        setErrorMessage("");
      }}
      onEmailChange={(value) => {
        setEmail(value);
        setErrorMessage("");
      }}
      onGoogleSignIn={() => void handleOAuthSignIn("google")}
      onMicrosoftSignIn={() => void handleOAuthSignIn("azure")}
      onSsoSignIn={() => void handleSSOSignIn()}
      onEmailSignIn={() => void handleEmailMagicLinkSignIn()}
      onResetMagicLink={() => setMagicLinkSentToEmail(null)}
    />
  );
};

function extractProjectIdFromProjectOrInterviewPage() {
  // Regular expression for a UUID
  const uuidRegex =
    "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

  // Regular expression for the URL path
  // It matches either /project/{UUID} or /project/{UUID}/interviews/{UUID}
  // and captures the first UUID
  const urlPathRegex = new RegExp(
    `^/project/(${uuidRegex})(?:/interviews/${uuidRegex})?$`,
  );

  const match = urlPathRegex.exec(window.location.pathname);
  return match ? match[1] : null;
}
